import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

function vehicleColor(v) {
  if (v.geofence_status === "breach") return "#ef4444";            // out of zone
  if (v.ignition === "ON" || (v.speed || 0) > 0) return "#16a34a"; // moving / ignition on
  return "#2563eb";                                                // parked
}

function bikeIcon(v, active) {
  const color = vehicleColor(v);
  const moving = v.ignition === "ON" || (v.speed || 0) > 0;
  const ring = active ? `<div style="position:absolute;inset:-6px;border-radius:50%;border:3px solid ${color};opacity:.9;"></div>` : "";
  const arrow = v.heading != null
    ? `<div style="position:absolute;top:13px;left:13px;width:0;height:0;transform:rotate(${v.heading}deg);transform-origin:0 0;">
         <div style="position:absolute;left:-5px;top:-20px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:13px solid ${color};"></div>
       </div>` : "";
  return L.divIcon({
    className: "fleet-marker",
    html: `<div style="position:relative;width:26px;height:26px;">
      ${ring}
      ${arrow}
      <div style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.22;${moving ? "animation:fleetpulse 1.6s ease-out infinite;" : ""}"></div>
      <div style="position:absolute;top:7px;left:7px;width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);"></div>
    </div>`,
    iconSize: [26, 26], iconAnchor: [13, 13], popupAnchor: [0, -12],
  });
}

const userIcon = L.divIcon({
  className: "user-marker",
  html: `<div style="position:relative;width:20px;height:20px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:#3b82f6;opacity:.25;animation:fleetpulse 2s ease-out infinite;"></div>
    <div style="position:absolute;top:5px;left:5px;width:10px;height:10px;border-radius:50%;background:#3b82f6;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>
  </div>`,
  iconSize: [20, 20], iconAnchor: [10, 10],
});

function hubIcon(isMain) {
  const fill = isMain ? "#0f766e" : "#475569";
  return L.divIcon({
    className: "hub-marker",
    html: `<div style="position:relative;width:34px;height:42px;filter:drop-shadow(0 3px 5px rgba(0,0,0,.45));">
      <svg width="34" height="42" viewBox="0 0 34 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 0C7.6 0 0 7.4 0 16.6 0 28 17 42 17 42s17-14 17-25.4C34 7.4 26.4 0 17 0Z" fill="${fill}"/>
        <circle cx="17" cy="16" r="11" fill="#ffffff"/>
        <path d="M11 17.5 17 12l6 5.5V23a1 1 0 0 1-1 1h-3v-3.5h-4V24h-3a1 1 0 0 1-1-1v-5.5Z" fill="${fill}"/>
      </svg>
      ${isMain ? `<span style="position:absolute;top:-4px;right:-4px;width:12px;height:12px;border-radius:50%;background:#FFCC00;border:2px solid #fff;"></span>` : ""}
    </div>`,
    iconSize: [34, 42], iconAnchor: [17, 42], popupAnchor: [0, -38],
  });
}

/** Guards every Leaflet call against a map whose container has already been
 *  removed (React.StrictMode double-mount / navigation mid-animation) — that
 *  race throws "Cannot read properties of undefined (reading '_leaflet_pos')". */
const mapAlive = (map) => !!(map && map._container && map._loaded);

function MapController({ focus, userLocation, points, markerRefs, initialView }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || !mapAlive(map)) return;
    try {
      if (initialView && initialView.fleet) { if (points.length) map.fitBounds(L.latLngBounds(points), { padding: [44, 44], maxZoom: 15 }); fitted.current = true; }
      else if (initialView && initialView.lat != null) { map.setView([initialView.lat, initialView.long], initialView.zoom || 15); fitted.current = true; }
      else if (userLocation) { map.setView(userLocation, 14); fitted.current = true; }
      else if (points.length) { map.fitBounds(L.latLngBounds(points), { padding: [44, 44], maxZoom: 15 }); fitted.current = true; }
    } catch { /* map torn down mid-render */ }
  }, [userLocation, points, map, initialView]);

  useEffect(() => {
    if (!focus || focus.lat == null || !mapAlive(map)) return;
    try { map.flyTo([focus.lat, focus.long], 16, { duration: 0.8 }); } catch { return; }
    const id = setTimeout(() => {
      const m = markerRefs.current[focus.id];
      if (m && mapAlive(map)) { try { m.openPopup(); } catch { /* marker removed */ } }
    }, 450);
    return () => clearTimeout(id);
  }, [focus, map, markerRefs]);
  return null;
}

function ago(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

export default function FleetMap({ geofence, geofencingEnabled, vehicles, trails, focus, userLocation, activeId, hubs, initialView }) {
  const positioned = vehicles.filter((v) => v.lat != null && v.long != null);
  const hubList = (hubs || []).filter((h) => h.lat != null && h.long != null);
  const markerRefs = useRef({});
  const points = useMemo(() => {
    const pts = positioned.map((v) => [v.lat, v.long]);
    if (geofence) pts.push([geofence.lat, geofence.long]);
    hubList.forEach((h) => pts.push([h.lat, h.long]));
    if (userLocation) pts.push(userLocation);
    return pts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positioned.length, geofence?.lat, geofence?.long, hubList.length, userLocation]);
  const center = userLocation || (hubList.find((h) => h.is_main) ? [hubList.find((h) => h.is_main).lat, hubList.find((h) => h.is_main).long] : geofence ? [geofence.lat, geofence.long] : [11.9139, 79.8145]);

  return (
    <div data-testid="live-map" className="overflow-hidden rounded-[28px] border border-tenant-border shadow-tenant-soft">
      <style>{`@keyframes fleetpulse{0%{transform:scale(.8);opacity:.5}70%{transform:scale(2.2);opacity:0}100%{opacity:0}}`}</style>
      <MapContainer center={center} zoom={13} scrollWheelZoom style={{ height: 560, width: "100%" }}>
        <TileLayer attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
        {geofencingEnabled && geofence && (
          <Circle center={[geofence.lat, geofence.long]} radius={(geofence.radius_km || 8) * 1000}
            pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.05, weight: 1.5, dashArray: "6 6" }} />
        )}
        {userLocation && (
          <Marker position={userLocation} icon={userIcon}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        {hubList.map((h) => (
          <Marker key={`hub-${h.id}`} position={[h.lat, h.long]} icon={hubIcon(h.is_main)}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <p style={{ fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                  {h.name}{h.is_main ? <span style={{ fontSize: 10, background: "#FFCC00", color: "#000", padding: "1px 6px", borderRadius: 999, fontWeight: 700 }}>MAIN</span> : null}
                </p>
                {h.address && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#666" }}>📍 {h.address}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
        {positioned.map((v) => {
          const path = (trails && trails[v.id]) || [];
          if (path.length < 2) return null;
          return (
            <Polyline key={`trail-${v.id}`} positions={path} pathOptions={{ color: vehicleColor(v), weight: 3, opacity: 0.55 }} />
          );
        })}
        {positioned.map((v) => (
          <Marker key={v.id} position={[v.lat, v.long]} icon={bikeIcon(v, v.id === activeId)}
            ref={(r) => { if (r) markerRefs.current[v.id] = r; }}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <p style={{ fontWeight: 600, margin: 0 }}>{v.reg_number}</p>
                <p style={{ margin: "2px 0 6px", color: "#666", fontSize: 12 }}>{v.model}{v.rider ? ` · ${v.rider}` : ""}</p>
                {v.address && <p style={{ margin: "0 0 6px", fontSize: 12 }}>📍 {v.address}</p>}
                <p style={{ margin: 0, fontSize: 12 }}>
                  {Math.round(v.speed || 0)} km/h · {v.ignition || "—"}{v.charge_pct != null ? ` · ${v.charge_pct}%` : ""} · {(v.total_km || 0).toLocaleString()} km
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>last fix {ago(v.gps_last_ping)}</p>
                {v.gps_fetched_at && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#888" }}>data fetched {ago(v.gps_fetched_at)}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
        <MapController focus={focus} userLocation={userLocation} points={points} markerRefs={markerRefs} initialView={initialView} />
      </MapContainer>
    </div>
  );
}
